import { useEffect, useMemo, useRef, useState } from 'react'
import type { DeviceSpecDevice, SpecMachine } from '../../types'
import { searchSpec } from './searchSpec'
import type { SearchHit } from './searchSpec'

// 명세서 검색창 — Ctrl+K 로 바로 입력. 디바이스·태그와 마스터데이터를 함께 찾고, 결과를 누르거나 ↑↓ + Enter 로 그 칸으로 이동한다 (다른 탭이면 탭도)
export default function SpecSearch({ devices, machines, onPick }: { devices: DeviceSpecDevice[]; machines: SpecMachine[]; onPick: (hit: SearchHit) => void }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [index, setIndex] = useState(0)
  const { hits, total } = useMemo(() => searchSpec(devices, query, 100, machines), [devices, machines, query])

  // Ctrl+K — 검색창으로
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
        inputRef.current?.select()
        setOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // 바깥을 누르면 닫는다
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => { if (!wrapRef.current?.contains(e.target as Node)) setOpen(false) }
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [open])

  const pick = (hit: SearchHit) => {
    onPick(hit)
    setOpen(false)
    inputRef.current?.blur()
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setIndex(i => Math.min(i + 1, hits.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIndex(i => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter' && hits[index]) { e.preventDefault(); pick(hits[index]) }
    else if (e.key === 'Escape') { if (query) setQuery(''); else { setOpen(false); inputRef.current?.blur() } }
  }

  const q = query.trim()
  return (
    <div ref={wrapRef} className="relative">
      <div className="flex items-center gap-1.5 border border-gray-300 rounded-lg px-2 py-1 bg-surface focus-within:border-blue-400 w-72">
        <svg className="w-3.5 h-3.5 text-gray-400 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.8}>
          <circle cx="7" cy="7" r="4.5" /><path d="M10.5 10.5 14 14" strokeLinecap="round" />
        </svg>
        <input ref={inputRef} value={query} placeholder="Alias·주소·설명·전송코드 검색"
          onChange={e => { setQuery(e.target.value); setIndex(0); setOpen(true) }}
          onFocus={() => setOpen(true)} onKeyDown={onKeyDown}
          className="flex-1 min-w-0 bg-transparent text-xs outline-none" />
        <kbd className="text-[10px] text-gray-500 font-mono">Ctrl K</kbd>
      </div>

      {open && q && (
        <div className="absolute left-0 top-full mt-1 z-40 w-[480px] max-h-96 overflow-y-auto rounded-lg border border-gray-200 bg-surface shadow-lg py-1">
          {hits.length === 0 ? (
            <div className="px-3 py-3 text-xs text-gray-500">'{q}' 와 일치하는 곳이 없습니다.</div>
          ) : (
            <>
              {hits.map((h, i) => (
                <button key={`${h.machine?.key ?? h.deviceKey}:${h.row}:${h.label}`} onMouseEnter={() => setIndex(i)} onClick={() => pick(h)}
                  className={`w-full text-left flex items-baseline gap-2 px-3 py-1.5 text-xs ${i === index ? 'bg-blue-50' : ''}`}>
                  {h.machine && <span className="shrink-0 px-1 rounded bg-gray-100 text-[10px] text-gray-600">마스터</span>}
                  <span className={`text-gray-700 truncate max-w-[120px] ${h.machine ? 'font-mono' : ''}`}>{h.deviceLabel}</span>
                  <span className="text-gray-500 shrink-0">{h.row != null ? `${h.row + 1}행 · ` : ''}{h.label}</span>
                  <span className="ml-auto font-mono text-gray-800 truncate max-w-[220px]"><Highlight text={h.value} query={q} /></span>
                </button>
              ))}
              <div className="px-3 pt-1 mt-1 border-t border-gray-100 text-[11px] text-gray-500">
                {total > hits.length ? `${total}곳 중 ${hits.length}곳 표시 · ` : `${total}곳 · `}↑↓ 고르기 · Enter 이동 · Esc 지우기
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function Highlight({ text, query }: { text: string; query: string }) {
  const i = text.toLowerCase().indexOf(query.toLowerCase())
  if (i < 0) return <>{text}</>
  return <>{text.slice(0, i)}<mark className="bg-amber-200 text-gray-900 rounded-sm">{text.slice(i, i + query.length)}</mark>{text.slice(i + query.length)}</>
}
